import { Dir } from '@angular/cdk/bidi';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    HostBinding,
    Input,
    Optional,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';

/**
    * Max length for property names. Any names bigger than this get trunctated.
    */
const KEY_MAX_LENGTH = 30;

@Component({
    selector: 'ui-json-formatter',
    templateUrl: './ui-json-formatter.component.html',
    styleUrls: ['./ui-json.formatter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiJsonFormatterComponent {
    @HostBinding('class.expandable')
    public isExpandable = false;

    /**
     * levelsOpen?: number
     * Levels opened by default when JS object is formatted and rendered.
     */
    @Input()
    public set levelsOpen(levelsOpen: number) {
        if (!Number.isInteger(levelsOpen)) {
            throw new Error('[levelsOpen] needs to be an integer.');
        }
        this._levelsOpen = levelsOpen;
        this._open = levelsOpen > 0;
    }
    public get levelsOpen(): number {
        return this._levelsOpen;
    }

    public get open(): boolean {
        return this._open;
    }

    /**
     * key?: string
     * Tag to be displayed next to formatted object.
     */
    @Input()
    public set key(key: string) {
        this._key = key;
    }
    public get key(): string {
        const elipsis: string = this._key && this._key.length > KEY_MAX_LENGTH ? '…' : '';
        return this._key ?
            this._key.substring(0, KEY_MAX_LENGTH) + elipsis
            : '';
    }

    /**
     * data: any
     * JS object to be formatted.
     */
    @Input()
    public set data(data: any) {
        this._data = data;
        this._parseChildren();
        this.isExpandable = this._children &&
            this._children.length > 0;
    }
    public get data(): any {
        return this._data;
    }

    @Input()
    public emptyPlaceholder = 'Empty';
    @Input()
    public objectPlaceholder = 'Object';

    public get children(): string[] {
        return this._children;
    }

    public get isRTL(): boolean {
        if (this._dir) {
            return this._dir.dir === 'rtl';
        }
        return false;
    }

    @Input()
    @ContentChild(TemplateRef, { static: true })
    public template!: TemplateRef<any>;

    private _key?: string;
    private _data: any;
    private _children!: string[];
    private _open = false;
    private _levelsOpen = 0;

    constructor(private _changeDetectorRef: ChangeDetectorRef,
        @Optional() private _dir: Dir) {
    }

    /**
     * Refreshes json-formatter and rerenders [data]
     */
    public refresh(): void {
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Toggles collapse/expanded state of component.
     */
    public toggle(): void {
        this._open = !this._open;
    }

    public isObject(): boolean {
        return this.getType(this._data) === 'object';
    }

    public isArray(): boolean {
        return Array.isArray(this._data);
    }

    public isEmpty(): boolean {
        const type: string = this.getType(this._data);
        if (type === 'string' && !this._data) {
            return true;
        }

        if (type === 'object' && !this.isExpandable) {
            return true;
        }

        return false;
    }

    public hasChildren(): boolean {
        return this._children && this._children.length > 0;
    }

    /**
     * Gets parsed value depending on value type.
     */
    public getValue(value: any): string {
        const type: string = this.getType(value);
        if (type === 'undefined' || (type === 'null')) {
            return type;
        }

        if (type === 'date') {
            value = new Date(value).toString();
        }

        if (type === 'function') {
            // Remove content of the function
            return value.toString()
                .replace(/[\r\n]/g, '')
                .replace(/\{.*\}/, '') + '{…}';
        }

        if (Array.isArray(value)) {
            return this.getObjectName() + ' [' + value.length + ']';
        }

        return value;
    }

    /**
     * Gets type of object.
     * returns 'null' if object is null and 'date' if value is object and can be parsed to a date.
     */
    public getType(object: any): string {
        if (typeof object === 'object') {
            if (!object) {
                return 'null';
            }
            if (Array.isArray(object)) {
                return 'object';
            }
            const date: Date = new Date(object);
            if (
                Object.prototype.toString.call(date) === '[object Date]' &&
                !Number.isNaN(date.getTime())
            ) {
                return 'date';
            }
        }
        return typeof object;
    }

    /**
     * Generates string representation depending if its an object or function.
     * see: http://stackoverflow.com/a/332429
     */
    public getObjectName(): string {
        const object: any = this._data;

        if (this.isObject() && !object.constructor) {
            return this.objectPlaceholder;
        }

        const funcNameRegex: RegExp = /function (.{1,})\(/;
        const results: RegExpExecArray = (funcNameRegex).exec((object).constructor.toString())!;

        if (results && results.length > 1) {
            return results[1] === 'Object' ? this.objectPlaceholder : results[1];
        }

        return '';
    }

    private _parseChildren(): void {
        if (this.isObject()) {
            this._children = [];
            for (const key in this._data) {
                if (this._data) {
                    this._children.push(key);
                }
            }
        }
    }
}
